import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import api from '../api';
import { isSinInput } from '../utils/applicantSin';

/**
 * Applicant enters their SIN through a one-time link from the property manager.
 */
const ProvideSinPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [linkError, setLinkError] = useState(null);
  const [sin, setSin] = useState('');
  const [confirmSin, setConfirmSin] = useState('');
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/api/applicant-sin/public/${encodeURIComponent(token)}`)
      .then((res) => {
        if (!cancelled) setInfo(res.data);
      })
      .catch((err) => {
        if (!cancelled) setLinkError(err.response?.data?.error || 'This link is not valid. Ask your property manager for a new one.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);
    if (!isSinInput(sin)) {
      setFormError('Enter the 9 digits of your SIN.');
      return;
    }
    if (sin.replace(/[\s-]/g, '') !== confirmSin.replace(/[\s-]/g, '')) {
      setFormError("The two entries don't match. Please check and try again.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/applicant-sin/public/${encodeURIComponent(token)}`, { sin });
      setSin('');
      setConfirmSin('');
      setDone(true);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Could not save your SIN. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (loading) {
    body = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (done) {
    body = (
      <Box sx={{ textAlign: 'center', py: 2 }} data-testid="sin-received">
        <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" gutterBottom>Thank you</Typography>
        <Typography variant="body1">
          Your SIN was saved securely with your application. You can close this page.
        </Typography>
      </Box>
    );
  } else if (linkError) {
    body = <Alert severity="warning">{linkError}</Alert>;
  } else {
    body = (
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {info?.firstName ? `Hi ${info.firstName}, your` : 'Your'} property manager asked for your Social
          Insurance Number (SIN) to run the credit check for your rental application
          {info?.propertyName ? ` at ${info.propertyName}` : ''}.
        </Typography>
        <TextField
          fullWidth
          label="SIN"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={sin}
          onChange={(e) => setSin(e.target.value)}
          sx={{ mb: 2 }}
          inputProps={{ 'data-testid': 'sin-input', 'data-clarity-mask': 'true', maxLength: 11 }}
        />
        <TextField
          fullWidth
          label="Enter your SIN again"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={confirmSin}
          onChange={(e) => setConfirmSin(e.target.value)}
          sx={{ mb: 2 }}
          inputProps={{ 'data-testid': 'sin-confirm', 'data-clarity-mask': 'true', maxLength: 11 }}
        />
        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
        <Button type="submit" variant="contained" fullWidth disabled={busy}>
          {busy ? 'Saving…' : 'Send securely'}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Your SIN is encrypted and only your property manager can view it. This link works once.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 520, mx: 'auto', mt: { xs: 2, sm: 6 } }}>
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LockIcon color="primary" />
            <Typography variant="h5" component="h1">Secure SIN form</Typography>
          </Box>
          {body}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProvideSinPage;
